import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useAuth, API_BASE } from "./AuthContext";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";


/**
 * TMXSettings
 * -----------
 * Lets a logged-in user:
 * - View all active TMX entries (UserTMX rows).
 * - Upload one TMX file per source/target pair (you can add multiple per pair).
 * - Soft-delete (disable) an existing TMX entry.
 *
 * Uses:
 *   GET    /tmx
 *   POST   /tmx/file   (file -> S3, returns { filename, s3_key })
 *   POST   /tmx        (metadata -> UserTMX)
 *   DELETE /tmx/{id}
 *
 * Also uses /lang_codes to present language NAMES instead of raw codes.
 */
export default function TMXSettings({ onBack }) {
  const { user, jwt } = useAuth();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Raw file selection
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);

  const [selectedPairIndex, setSelectedPairIndex] = useState(null);

  const fileInputRef = useRef(null);

  // Language mapping from /lang_codes
  const [langCodeMap, setLangCodeMap] = useState({});
  const [langNameToCode, setLangNameToCode] = useState({});

  // (removed unused languageList memo)

  // --- Custom workflow language pairs (to constrain dropdowns) ---
  const [workflowPairs, setWorkflowPairs] = useState([]);
  const [workflowPairsLoaded, setWorkflowPairsLoaded] = useState(false);

  useEffect(() => {
    if (!jwt || !user || !user.uses_custom_workflow) {
      setWorkflowPairs([]);
      setWorkflowPairsLoaded(false);
      return;
    }

    let cancelled = false;

    async function fetchWorkflowPairs() {
      try {
        const res = await axios.get(`${API_BASE}/workflow/lang_pairs`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        // Backend returns { pairs: [...] }, so read res.data.pairs
        const pairs =
          res.data && Array.isArray(res.data.pairs) ? res.data.pairs : [];
        if (!cancelled) {
          setWorkflowPairs(pairs);
          setWorkflowPairsLoaded(true);
        }
      } catch (err) {
        console.error("Failed to load workflow language pairs:", err);
        if (!cancelled) {
          setWorkflowPairs([]);
          setWorkflowPairsLoaded(true);
        }
      }
    }

    fetchWorkflowPairs();

    return () => {
      cancelled = true;
    };
  }, [jwt, user]);

  // Helper: display a nice language name from a code or name
  function displayLang(codeOrName) {
    if (!codeOrName) return "";
    if (langCodeMap[codeOrName]) return langCodeMap[codeOrName];
    const lc = codeOrName.toLowerCase().replace(/_/g, "-");
    for (const [code, name] of Object.entries(langCodeMap)) {
      if (code.toLowerCase().replace(/_/g, "-") === lc) return name;
    }
    return codeOrName;
  }

  // Build valid display-name pairs from workflowPairs for radio selection
  const validPairs = React.useMemo(() => {
    if (!Array.isArray(workflowPairs) || workflowPairs.length === 0) return [];
    return workflowPairs
      .map((p) => ({
        source: displayLang(p.source),
        target: displayLang(p.target),
        sourceCode: p.source, // keep originals too
        targetCode: p.target,
      }))
      .filter((p) => p.source && p.target && p.source !== p.target);
  }, [workflowPairs, langCodeMap]);

  // Build dropdown options from validPairs (sorted by source, then target)
  const pairOptions = React.useMemo(() => {
    const opts = validPairs.map((p, idx) => ({
      ...p,
      label: `${p.source} \u2192 ${p.target}`,
      _idx: idx, // keep link to original validPairs index
    }));
    opts.sort((a, b) => {
      const s = a.source.localeCompare(b.source);
      return s !== 0 ? s : a.target.localeCompare(b.target);
    });
    return opts;
  }, [validPairs]);

  // If there's exactly one pair, default-select it
  React.useEffect(() => {
    if (validPairs.length === 1) {
      setSelectedPairIndex(0);
    }
  }, [validPairs.length]);

  const workflowMode = !!(user && user.uses_custom_workflow);

  const loadEntries = () => {
    if (!jwt) {
      setEntries([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    axios
      .get(`${API_BASE}/tmx`, {
        headers: { Authorization: `Bearer ${jwt}` },
      })
      .then((res) => {
        setEntries(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load TMX entries:", err);
        setError("Failed to load TMX entries.");
        setLoading(false);
      });
  };

  // Load TMX entries
  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line
  }, [jwt]);

  // Load language mappings (names <-> codes) from backend /lang_codes
  useEffect(() => {
    async function fetchLangCodes() {
      try {
        const resp = await fetch(`${API_BASE}/lang_codes`);
        const data = await resp.json();
        setLangCodeMap(data.code_to_name || {});
        setLangNameToCode(data.name_to_code || {});
      } catch (err) {
        console.error("Failed to load language codes:", err);
      }
    }
    fetchLangCodes();
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setFile(f || null);
    setFileName(f ? f.name || "" : "");
  };

  const resetForm = () => {
    setSelectedPairIndex(null);
    setFile(null);
    setFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!jwt) {
      alert("You must be logged in to manage data usage / TMX.");
      return;
    }

    if (selectedPairIndex === null || !validPairs[selectedPairIndex]) {
      alert("Please choose a language pair for this TMX file.");
      return;
    }
    if (!file) {
      alert("Please select a TMX file (.tmx).");
      return;
    }
    const lower = (file.name || "").toLowerCase();
    if (!lower.endsWith(".tmx")) {
      alert("Unsupported file type. Please upload a TMX file (.tmx).");
      return;
    }

    const picked = validPairs[selectedPairIndex];
    // Convert display names -> canonical codes before sending
    const srcCode = langNameToCode[picked.source] || picked.sourceCode || picked.source;
    const tgtCode = langNameToCode[picked.target] || picked.targetCode || picked.target;

    // --- Detect TMX languages client-side and compare against the selected pair ---
    const detectTMXLangs = async (f) => {
      const text = await f.text();
      const langs = new Set();
      const re = /<tuv\b[^>]*?\bxml:lang\s*=\s*["']([^"']+)["']/gi;
      let m;
      while ((m = re.exec(text))) {
        const lang = String(m[1] || "").trim();
        if (lang) {
          const clean = lang.toLowerCase().replace(/_/g, "-").split("-")[0];
          langs.add(clean);
          if (langs.size > 3) break;
        }
      }
      return Array.from(langs);
    };

    const normalizeCode = (code = "") => {
      const clean = String(code || "").toLowerCase().replace(/_/g, "-");
      return clean.split("-")[0];
    };

    let overrideAccepted = false;
    try {
      const langs = await detectTMXLangs(file); // e.g., ["en","de"]
      if (langs.length === 0) {
        if (!window.confirm("No languages detected in this TMX. Continue anyway?")) {
          return;
        }
        overrideAccepted = true;
      } else if (langs.length > 2) {
        alert("This TMX file contains more than two languages. Please provide a TMX file with only one source and one target language.");
        return;
      } else if (langs.length === 2) {
        const srcSel = normalizeCode(srcCode);
        const tgtSel = normalizeCode(tgtCode);
        const tmxSet = new Set(langs);
        const mismatch =
          !(tmxSet.has(srcSel) && tmxSet.has(tgtSel)) &&
          !(tmxSet.has(tgtSel) && tmxSet.has(srcSel));
        if (mismatch) {
          const ok = window.confirm(
            "The languages in this TMX file do not match your selected language pair. You can override the TMX languages with your selected pair. Continue?"
          );
          if (!ok) return;
          overrideAccepted = true;
        }
      }
    } catch (err) {
      console.warn("TMX language detection failed:", err);
      // Non-fatal; continue
    }

    setSaving(true);
    setError("");

    try {
      // 1) Upload raw file -> /tmx/file
      const fd = new FormData();
      fd.append("file", file);

      const uploadRes = await axios.post(`${API_BASE}/tmx/file`, fd, {
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const { filename, s3_key } = uploadRes.data || {};
      if (!filename || !s3_key) {
        throw new Error("Upload did not return filename or s3_key.");
      }

      // 2) Create UserTMX row -> /tmx
      const payload = {
        source_code: srcCode,
        target_code: tgtCode,
        filename,
        s3_key,
        ...(overrideAccepted ? { override_langs: true } : {}),
      };

      await axios.post(`${API_BASE}/tmx`, payload, {
        headers: { Authorization: `Bearer ${jwt}` },
      });

      resetForm();
      loadEntries();
      alert("TMX file committed for this language pair.");
    } catch (err) {
      console.error("Failed to commit TMX file:", err);
      const detail =
        err?.response?.data?.detail || "Failed to commit TMX file. Please try again.";
      setError(detail);
      alert(detail);
    } finally {
      setSaving(false);
    }
  };


  const handleDelete = async (entry) => {
    if (!jwt) {
      alert("You must be logged in to manage data usage / TMX.");
      return;
    }
    if (
      !window.confirm(
        `Remove TMX "${entry.filename}" for ${displayLang(
          entry.source_code
        )} → ${displayLang(entry.target_code)}?`
      )
    ) {
      return;
    }
    try {
      await axios.delete(`${API_BASE}/tmx/${entry.id}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      loadEntries();
    } catch (err) {
      console.error("Failed to delete TMX entry:", err);
      const detail =
        err?.response?.data?.detail ||
        "Failed to disable TMX entry.";
      alert(detail);
    }
  };

  // Group entries by display source/target name
  const grouped = entries.reduce((acc, e) => {
    const key = `${displayLang(e.source_code || "")} → ${displayLang(
      e.target_code || ""
    )}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto mt-8 px-4">
        <h1 className="text-2xl font-semibold mb-2">
          Data Usage / LLM Training
        </h1>
        <p className="text-sm text-gray-700">
          Please sign in to manage your data sharing and previous translations (TMX) settings.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto mt-8 px-4 pb-10">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mb-4 text-sm underline"
          style={{ color: "#00a7e1" }}
        >
          ← Back to Workflow Settings
        </button>
      )}
      <h1 className="text-2xl font-semibold mb-2">
        Data Usage / LLM Translation Training
      </h1>

      <p className="text-sm text-gray-700 mb-4">
        Attach TMX files with your previous translations so they can be used for
        translation training for your language pairs. You can add multiple TMX
        files for the same language pair.
      </p>

      {/* Create / upload form */}
      <div className="mb-8 border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
        <h2 className="text-lg font-semibold mb-3">Add TMX File</h2>
        <form onSubmit={handleCreate} className="space-y-3">

        <div className="mt-1">
          <div className="text-sm font-semibold text-gray-900 mb-2">
            Language Pair
          </div>

          {pairOptions.length === 0 ? (
            <div className="text-sm text-gray-600">
              No custom language pairs found. Please edit your workflow.
            </div>
          ) : (
            <Autocomplete
              options={pairOptions}
              getOptionLabel={(opt) => opt.label}
              value={
                (selectedPairIndex !== null &&
                 pairOptions.find(o => o._idx === selectedPairIndex)) || null
              }
              onChange={(event, newValue) => {
                if (!newValue) {
                  setSelectedPairIndex(null);
                  return;
                }
                setSelectedPairIndex(newValue._idx);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Select Language Pair"
                  variant="outlined"
                  placeholder={
                    params.inputProps.value === "" && selectedPairIndex === null
                      ? "Language Pair"
                      : ""
                  }
                />
              )}
              sx={{
                marginBottom: 1,
                maxWidth: 520,
                width: "100%",
                "& .MuiInputBase-input": { fontSize: "0.875rem" },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                "& .MuiOutlinedInput-root": { borderRadius: "12px" },
              }}
            />
          )}
        </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              TMX File (.tmx)
            </label>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="rounded-xl px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
              style={{ backgroundColor: "#00a7e1" }}
            >
              {fileName ? "Change TMX File" : "Choose TMX File"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".tmx"
              onChange={handleFileChange}
              className="hidden"
            />
            {fileName && (
              <div className="mt-2 flex items-center gap-3 text-xs text-gray-700">
                <span>
                  Selected:{" "}
                  <span className="font-semibold">{fileName}</span>
                </span>
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-xs text-red-600 underline"
                >
                  Remove
                </button>
              </div>
            )}
            <p className="mt-2 text-xs text-gray-600">
              TMX files should contain aligned segments for a single language
              pair (source and target).
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving || !file}
              className={`text-white px-4 py-2 rounded-xl text-sm font-semibold ${saving || !file ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
              style={{ backgroundColor: "#00a7e1" }}
            >
              {saving ? "Saving..." : "Commit TMX for Language Pair"}
            </button>
            {error ? (
              <span className="text-xs text-red-600">{error}</span>
            ) : !file ? (
              <span className="text-xs text-gray-500">Please select a TMX file.</span>
            ) : null}
          </div>
        </form>
      </div>

      {/* Existing TMX entries */}
      <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
        <h2 className="text-lg font-semibold mb-3">TMX Files</h2>

        {loading ? (
          <p className="text-sm text-gray-600">Loading TMX entries…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-gray-600">
            You haven&apos;t added any TMX files yet.
          </p>
        ) : (
          <div className="space-y-4">
            {Object.entries(grouped).map(([pair, rows]) => (
              <div key={pair} className="border border-gray-100 rounded-lg p-3">
                <div className="text-sm font-semibold mb-2">{pair}</div>
                <div className="space-y-2">
                  {rows.map((e) => (
                    <div
                      key={e.id}
                      className="flex items-start justify-between gap-3 border-t border-gray-100 pt-2 first:border-t-0 first:pt-0"
                    >
                      <div className="text-xs text-gray-700">
                        <div>
                          <span className="font-semibold">File:</span>{" "}
                          {e.filename || "(no filename)"}
                        </div>
                        <div>
                          <span className="font-semibold">Status:</span>{" "}
                          {e.is_active ? "Active" : "Inactive"}
                        </div>
                        {e.created_at && (
                          <div>
                            <span className="font-semibold">Created:</span>{" "}
                            {String(e.created_at)}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <button
                          type="button"
                          onClick={() => handleDelete(e)}
                          className="px-3 py-1 rounded text-xs text-white"
                          style={{ backgroundColor: "#dc2626" }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
